import type { Balance } from '@gamble-chess/contracts';
import type { JSX } from 'react';

import { useLedger } from '../api/stakes.ts';
import { counterpartyKey, describeDirection, formatAmount } from '../format.ts';
import { Card } from '../ui/Card.tsx';
import { Loaded } from '../ui/Loaded.tsx';
import { hintClass } from '../ui/styles.ts';

function balanceKey({ counterparty, unit }: Balance): string {
  return `${counterpartyKey(counterparty)}-${unit}`;
}

/** One open balance, the size first: that is the number the screen exists to show. */
function BalanceTile({ balance }: { readonly balance: Balance }): JSX.Element {
  const owed = balance.amount > 0;

  return (
    <li className="flex flex-col gap-1 rounded-lg border border-border bg-surface-sunken px-4 py-3">
      <span className={`text-3xl font-semibold tabular-nums ${owed ? 'text-debt' : 'text-credit'}`}>
        {formatAmount(balance.amount, balance.unit)}
      </span>
      <span className="text-sm font-medium text-ink-muted">
        {describeDirection(balance.counterparty, balance.amount)}
      </span>
    </li>
  );
}

/** The first thing on the first screen: what is owed, and to whom. */
export function Balances(): JSX.Element {
  const ledger = useLedger();

  return (
    <Card
      title="What you owe"
      description="Losses under a standing rule and settled wagers land here; work takes them down again."
    >
      <Loaded
        query={ledger}
        loading="Loading your balances…"
        error="Your balances could not be loaded. Please reload the page."
      >
        {({ balances }) =>
          balances.length === 0 ? (
            <p className={hintClass}>Nothing owed either way.</p>
          ) : (
            <ul aria-label="Balances" className="grid gap-3 sm:grid-cols-2">
              {balances.map((balance) => (
                <BalanceTile key={balanceKey(balance)} balance={balance} />
              ))}
            </ul>
          )
        }
      </Loaded>
    </Card>
  );
}
